import { useEffect } from "react";

/**
 * Google Analytics 4 (gtag.js), loaded on the HOME PAGE ONLY.
 *
 * The snippet deliberately does not live in public/index.html: that file is the
 * shell for every route (React Router serves them all from it, and
 * seo-render.php re-serves it to crawlers), so a tag in that head would load
 * Analytics site-wide. Rendering this component from src/pages/Home.js instead
 * means the script is requested only when the home page is shown - open
 * /read/20 or /news/some-slug directly and nothing Google-related is fetched.
 *
 * Two behaviours worth knowing before changing anything here:
 *
 * 1. gtag.js cannot be unloaded. Once a visitor has seen the home page the
 *    script stays resident for the rest of that browsing session, so if they
 *    then navigate to an article *without a full page reload* Google may still
 *    record it (GA4 Enhanced Measurement listens for history changes). That is
 *    a property of single page apps, not something this component can undo.
 *    A visitor who never opens the home page is never tracked at all.
 *
 * 2. It loads exactly once. Re-running gtag('config', ...) on a second visit to
 *    "/" would register the measurement ID twice and double count every event
 *    from then on, so return visits only send a page_view.
 */

const MEASUREMENT_ID =
    process.env.REACT_APP_GA_MEASUREMENT_ID || "G-00M0EC6Y0S";

const SCRIPT_ATTR = "data-ga-home";

/** Module level, not state: this tracks the browser session, not a render. */
let gtagLoaded = false;
let lastReportedPath = null;

const GoogleAnalytics = ({ measurementId = MEASUREMENT_ID }) => {
    useEffect(() => {
        if (!measurementId) return;

        const path = window.location.pathname + window.location.search;

        if (gtagLoaded) {
            // Back on "/" after visiting another route. StrictMode also
            // re-invokes this effect on mount in development, hence the path
            // check - without it the same view would be counted twice.
            if (path !== lastReportedPath && typeof window.gtag === "function") {
                window.gtag("event", "page_view", {
                    page_location: window.location.href,
                    page_title: document.title,
                });
                lastReportedPath = path;
            }
            return;
        }

        const loader = document.createElement("script");
        loader.async = true;
        loader.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        loader.setAttribute(SCRIPT_ATTR, "");
        document.head.appendChild(loader);

        const inline = document.createElement("script");
        inline.setAttribute(SCRIPT_ATTR, "");
        inline.text = [
            "window.dataLayer = window.dataLayer || [];",
            "function gtag(){dataLayer.push(arguments);}",
            "gtag('js', new Date());",
            `gtag('config', '${measurementId}');`,
        ].join("\n");
        document.head.appendChild(inline);

        gtagLoaded = true;
        lastReportedPath = path;
    }, [measurementId]);

    return null;
};

export default GoogleAnalytics;
